import React from 'react';
import { Link } from 'react-router-dom';

export const Model = () => (
  <section className="model" id="model" data-label="The Model" data-testid="model-section">
    <div className="model-wrap">
      <div className="model-intro reveal">
        <div className="eyebrow center">Our Model</div>
        <h2 className="sec-h">Where Public Mission Meets Private Execution</h2>
        <p className="sec-p">The structural failure of correctional education wasn't a funding problem. It was an architecture problem. Nonprofits hold the trust but can't move fast enough. For-profits move fast but can't access the grants or the mission credibility. AssistivOS is what happens when both work together.</p>
      </div>
      <div className="model-grid reveal">
        <div className="m-col np" data-drift="0.04">
          <span className="m-tag">Nonprofit Institution</span>
          <div className="m-org">HDG University</div>
          <div className="m-sub">HDGU · hdguniversity.com · Plano, Texas</div>
          <p className="m-body">HDG University is the mission anchor — the nonprofit institution that holds the educational relationships, federal grant eligibility, DOC procurement trust, and community credibility that Workforce Pell compliance, college partnerships, and second-chance hiring programs require to function.</p>
          <ul className="m-list">
            <li><span className="m-arr">→</span>Federal grant eligibility: Title I Part D, OJJDP, WIOA, BJA Second Chance Act</li>
            <li><span className="m-arr">→</span>Workforce Pell compliance and Title IV accreditation pathway</li>
            <li><span className="m-arr">→</span>Programs in Advanced Manufacturing, AI, Cybersecurity, and Skilled Trades</li>
            <li><span className="m-arr">→</span>DOC institutional trust and community procurement credibility</li>
            <li><span className="m-arr">→</span>150 annual full scholarships targeting underserved communities</li>
          </ul>
        </div>
        <div className="m-div"><div className="m-plus" aria-hidden="true">+</div></div>
        <div className="m-col fp" data-drift="0.07">
          <span className="m-tag">For-Profit Execution</span>
          <div className="m-org">Tech Tradesmith Institute</div>
          <div className="m-sub">TTI · Private Sector · Technology Platform · Growth Capital</div>
          <p className="m-body">Tech Tradesmith Institute provides the operational agility, capital structure, and technology execution that pure nonprofit models cannot sustain — enabling equity investment, accelerated build timelines, employer marketplace development, and the commercial partnerships that expand platform reach beyond any single grant cycle.</p>
          <ul className="m-list">
            <li><span className="m-arr">→</span>Equity investment structure enabling seed → Series A trajectory</li>
            <li><span className="m-arr">→</span>AI-accelerated platform development (5–7 month production build)</li>
            <li><span className="m-arr">→</span>38-organization North Central Texas employer partner network</li>
            <li><span className="m-arr">→</span>SaaS licensing to DOCs, colleges, and training providers</li>
            <li><span className="m-arr">→</span>Six-stream revenue architecture — no single point of failure</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const OUTCOME_CELLS = [
  { n: '43%', t: 'Reduction in Reincarceration', d: "The documented reduction in reincarceration odds for individuals who participate in correctional education — RAND's most rigorous finding. AssistivOS makes this the standard, not the exception.", src: 'Source: RAND Corporation Meta-Analysis' },
  { n: '$5', t: 'Returned Per $1 Invested', d: 'Every dollar invested in correctional education generates five in avoided reincarceration costs. At 5,000 learners, this becomes the argument for sustained public investment at the state and federal level.', src: 'Source: RAND Corporation Cost-Benefit Analysis' },
  { n: '$40.8B+', t: 'In Federal Pell Grant Capacity', d: 'Workforce Pell restoration draws from the $40.8B federal Pell Grant fund — with compliance requirements that no existing platform was designed to meet. AssistivOS was built from day one to meet every one of them.', src: 'Source: U.S. Department of Education, July 2023' },
  { n: '$217.8M', t: 'Market TAM at Inflection Point', d: 'The prison-edtech total addressable market sits between $217.8M and $923.7M — at a structural inflection point where policy is moving toward outcomes, incumbents are exposed, and no neutral infrastructure layer exists. Until AssistivOS.', src: 'Source: Jobs for the Future, 2024 Market Scan' },
];

export const Outcomes = () => (
  <section className="outcomes" id="outcomes" data-label="Outcomes" data-testid="outcomes-section">
    <div className="outcomes-wrap">
      <div className="outcomes-intro">
        <div className="oi-l reveal-l">
          <div className="eyebrow">Anticipated Outcomes</div>
          <h2 className="sec-h" style={{ color: 'var(--wh)' }}>The Numbers the Field Has Been Waiting for the Infrastructure to Produce.</h2>
        </div>
        <div className="oi-r reveal-r">
          <p>These are not projections pulled from optimism. They are documented outcomes of correctional education — validated by three decades of research — finally made deployable by a platform built to deliver, track, and prove them at scale.</p>
          <p>AssistivOS doesn't create these numbers. It creates the infrastructure to make them real for every learner, in every facility, for every funder who has ever asked: "Can you prove it worked?"</p>
        </div>
      </div>
      <div className="oc-grid reveal">
        {OUTCOME_CELLS.map((o) => (
          <div className="oc gb" key={o.t}>
            <span className="oc-n" data-final={o.n}>{o.n}</span>
            <div className="oc-t">{o.t}</div>
            <p className="oc-d">{o.d}</p>
            <div className="oc-src">{o.src}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Ask = ({ onBrief }) => (
  <section className="ask" id="ask" data-label="Invest" data-testid="ask-section">
    <div className="ask-wrap">
      <div className="ask-intro reveal">
        <div className="eyebrow center">Fund What Works</div>
        <h2 className="sec-h">Two Paths In. One Platform Out.</h2>
        <p className="sec-p">Whether you lead with capital or with grants, the outcome is the same — a production-ready platform delivering the correctional education infrastructure America has needed for two decades.</p>
      </div>
      <div className="ask-grid">
        <div className="ask-card inv gb tilt reveal-l" data-testid="ask-investors">
          <span className="a-tag">For Investors</span>
          <h3 className="a-h">Seed the Platform That Owns the Infrastructure Layer</h3>
          <p className="a-body">Correctional education is a $217.8M–$923.7M market at a structural inflection point. Workforce Pell restored. Incumbents exposed. No neutral infrastructure built yet. AssistivOS is raising the capital to finish it.</p>
          <span className="a-amt">$150K–$200K</span>
          <div className="a-amt-s">Seed round · AI-accelerated build · 5–7 month production timeline</div>
          <ul className="a-list">
            <li>Production-ready platform via AI-accelerated hybrid team</li>
            <li>First DOC facility contract by Month 9</li>
            <li>First Workforce Pell enrollment by Month 12</li>
            <li>$575K ARR at operational sustainability milestone</li>
            <li>Series A readiness: $1.5M ARR at 18–24 months post-seed</li>
          </ul>
          <button type="button" className="abtn sol" data-track="ask-invest" data-testid="ask-cta-invest" onClick={onBrief}>Download Investment Brief ↓</button>
        </div>
        <div className="ask-card gr gb tilt reveal-r" data-testid="ask-grantors">
          <span className="a-tag">For Grantors</span>
          <h3 className="a-h">Fund the Infrastructure the Field Has Always Needed</h3>
          <p className="a-body">HDG University leads grant applications across every major correctional education funding stream — with Tech Tradesmith Institute as the technology delivery arm and Goodwill North Central Texas as fiscal partner for federal fund access. Grant capital extends runway without dilution.</p>
          <span className="a-amt" style={{ color: 'var(--lt1)' }}>$50K–$900K</span>
          <div className="a-amt-s">Grant-eligible · Nonprofit fiscal structure · Federal compliance-ready</div>
          <ul className="a-list">
            <li>BJA Second Chance Act — up to $900K per award cycle</li>
            <li>DOL Pathway Home and RESTART workforce grants</li>
            <li>TWC reentry and Texas correctional workforce programs</li>
            <li>WIOA Title I and Title II workforce development funding</li>
            <li>Perkins V CTE grants via TEA for JJAEP and facility programs</li>
          </ul>
          <a href="mailto:info@hdguniversity.org?subject=AssistivOS%20Grant%20Partnership" className="abtn out" data-track="ask-grant" data-testid="ask-cta-grant">Explore Grant Partnership →</a>
        </div>
        <div className="ask-card tilt reveal" data-testid="ask-employers" style={{ gridColumn: '1 / -1', background: 'rgba(15,39,68,0.96)', border: '1px solid rgba(249,115,22,0.25)' }}>
          <span className="a-tag" style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}>For Employers</span>
          <h3 className="a-h" style={{ color: '#FFFFFF' }}>Hire Credential-Verified, Workforce-Ready Talent</h3>
          <p className="a-body" style={{ color: 'rgba(255,255,255,0.7)' }}>Access a growing pipeline of participants who have earned industry-recognized credentials — NCCER, OSHA-10, CompTIA AI+, EPA 608, and more — through structured workforce pathways. Introductions are brokered by case managers. No cold outreach, no compliance risk.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {['Federal Bonding', 'WOTC-eligible', 'Fair Chance pledge', 'Second-chance hiring'].map(tag => (
              <span key={tag} style={{ background: 'rgba(249,115,22,0.12)', color: '#F97316', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{tag}</span>
            ))}
          </div>
          <a href="/signup" className="abtn sol" data-track="ask-employer-join" data-testid="ask-cta-employer" style={{ background: '#F97316', color: '#0F2744', textDecoration: 'none', display: 'inline-block' }}>Join the Talent Marketplace →</a>
        </div>
      </div>
    </div>
  </section>
);

const TRACTION_CELLS = [
  { k: 'employer_partners', suffix: '', label: 'Employer Partners', sub: 'Second-chance network · North Central Texas', fallback: 38 },
  { k: 'platform_roles', suffix: '', label: 'Platform Roles', sub: 'Every stakeholder, walkable in the prototype', fallback: 7 },
  { k: 'prototype_sessions', suffix: '', label: 'Prototype Visits', sub: 'Tracked live on this site', live: true, fallback: 0 },
  { k: 'revenue_streams', suffix: '', label: 'Revenue Streams', sub: 'No single point of failure', fallback: 6 },
];

export const Traction = () => {
  const [data, setData] = React.useState(null);
  const rootRef = React.useRef(null);
  const ranRef = React.useRef(false);

  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/traction`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({}));
  }, []);

  React.useEffect(() => {
    if (!data || ranRef.current) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting || ranRef.current) return;
        ranRef.current = true;
        io.disconnect();
        el.querySelectorAll('[data-count]').forEach((n) => {
          const final = parseInt(n.dataset.count, 10) || 0;
          if (reduced || final === 0) { n.textContent = String(final); return; }
          const start = performance.now();
          const dur = 1100;
          const frame = (now) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            n.textContent = String(Math.round(final * eased));
            if (t < 1) requestAnimationFrame(frame);
          };
          requestAnimationFrame(frame);
        });
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [data]);

  return (
    <section className="traction" id="traction" data-label="Traction" data-testid="traction-section" ref={rootRef}>
      <div className="traction-wrap reveal">
        <div className="tr-head">
          <span className="tr-eyebrow">Traction, Counted</span>
          <span className="tr-sub">Live numbers behind the answers below</span>
        </div>
        <div className="tr-grid">
          {TRACTION_CELLS.map((c) => {
            const val = data && typeof data[c.k] === 'number' ? data[c.k] : c.fallback;
            return (
              <div className="tr-cell" key={c.k} data-testid={`traction-${c.k}`}>
                <span className="tr-n">
                  <span data-count={val}>0</span>
                  {c.live && <span className="tr-live" aria-label="live count"><span className="tr-live-dot" />LIVE</span>}
                </span>
                <div className="tr-t">{c.label}</div>
                <div className="tr-d">{c.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  {
    q: 'What exactly is the raise, and what does it fund?',
    a: 'A $150K–$200K seed round funding the AI-accelerated production build (5–7 months), first DOC facility contract by Month 9, and first Workforce Pell enrollment by Month 12. The milestone target is $575K ARR at operational sustainability, with Series A readiness at $1.5M ARR within 18–24 months post-seed.',
  },
  {
    q: 'How does a company with a nonprofit partner return capital to investors?',
    a: 'Equity sits in Tech Tradesmith Institute — the for-profit technology and execution arm. HDG University (nonprofit) anchors grant eligibility, Pell compliance, and DOC trust, which de-risks the business and extends runway without dilution. Investors hold TTI equity; grants never dilute it.',
  },
  {
    q: 'How does AssistivOS make money?',
    a: 'Six streams with no single point of failure: DOC facility licensing, Workforce Pell tuition flow through college partners, marketplace fees from training providers, employer subscriptions for the matched-candidate pipeline, compliance/reporting tools, and ethical sponsorship. Facility licensing and Pell flow anchor the first $575K ARR.',
  },
  {
    q: 'Why is now the moment — after 30 years of failed attempts?',
    a: "July 2023: Workforce Pell was fully restored, drawing from $40.8B+ in Federal Pell Grant Capacity — the largest policy shift in a generation. Every incumbent was built for the old content-catalog era. Nobody has built the neutral, Pell-compliant infrastructure layer the new rules require. That's the gap AssistivOS occupies.",
  },
  {
    q: 'How is this different from JPay, Edovo, and the tablet incumbents?',
    a: "They sell content catalogs and hardware. AssistivOS is the vendor-agnostic operating system on top: credential clock-hour tracking, employer matching before release, reentry planning, and one-click WIOA/DOL/Pell evidence reporting — for every stakeholder, on the tablets facilities already own. Incumbents become distribution, not competition.",
  },
  {
    q: 'What traction exists today?',
    a: 'A fully walkable live prototype covering all seven user roles (you can open it right now), a 38-organization second-chance employer network in North Central Texas, the HDGU + TTI dual structure in place, and Goodwill North Central Texas as fiscal partner for federal fund access. Grant applications across BJA, DOL, and TWC pipelines are in motion.',
  },
];

const FaqItem = ({ item, idx, open, onToggle }) => (
  <div className={`faq-item${open ? ' open' : ''}`}>
    <button type="button" className="faq-q" data-track={`faq-${idx}`} data-testid={`faq-q-${idx}`} aria-expanded={open} onClick={onToggle}>
      <span>{item.q}</span>
      <span className="faq-ic" aria-hidden="true">{open ? '−' : '+'}</span>
    </button>
    <div className="faq-a" data-testid={`faq-a-${idx}`}><p>{item.a}</p></div>
  </div>
);

export const FAQ = ({ onSchedule }) => {
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <section className="faq" id="faq" data-label="FAQ" data-testid="faq-section">
      <div className="faq-wrap">
        <div className="faq-head reveal-l">
          <div className="eyebrow">Before You Book a Call</div>
          <h2 className="sec-h" style={{ color: 'var(--lt1)' }}>The Questions Funders Always Ask.</h2>
          <p className="sec-p" style={{ color: 'var(--lt2)', marginTop: 14 }}>Straight answers, no deck required. Anything missing? That's what the call is for.</p>
          <button type="button" className="abtn sol" style={{ marginTop: 24 }} data-track="faq-schedule" data-testid="faq-cta-call" onClick={onSchedule}>Schedule a Call →</button>
        </div>
        <div className="faq-list reveal-r" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <FaqItem key={i} item={f} idx={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export const CloseBanner = ({ onSchedule }) => (
  <div className="close-banner" data-testid="close-banner">
    <div className="close-wrap">
      <h2 className="close-h">The Gate Is Open. The Platform Is Ready. The Question Is Who Builds It With Us.</h2>
      <p className="close-p">The evidence has always been there. The policy window is open now. The technology exists. What correctional education has never had — until AssistivOS — is the infrastructure to put all three together at once.</p>
      <div className="close-btns">
        <Link to="/prototype" className="cb-p magnetic" data-track="close-platform" data-testid="close-cta-platform">See the Platform Live →</Link>
        <button type="button" className="cb-s magnetic" data-track="close-schedule" data-testid="close-cta-call" onClick={onSchedule}>Schedule a Conversation</button>
      </div>
    </div>
  </div>
);

export const Footer = ({ onSchedule, onBrief }) => (
  <footer data-testid="mkt-footer">
    <div className="f-grid">
      <div>
        <div className="f-brand"><img src="/logo.png" alt="AssistivOS" style={{ height: 34, width: 'auto', display: 'block', marginBottom: 12 }} /></div>
        <p className="f-tagline">The operating system for correctional education. Built at the intersection of HDG University's nonprofit mission and Tech Tradesmith Institute's private-sector execution.</p>
        <div className="f-contact">
          info@hdguniversity.org<br />
          (945) 289-9371<br />
          <Link to="/prototype">Live Prototype</Link><br />
          2600 K Avenue, Suite 101 · Plano, TX 75074
        </div>
      </div>
      <div>
        <div className="f-col-t">Platform</div>
        <ul className="f-links">
          <li><Link to="/prototype">Learner Experience</Link></li>
          <li><Link to="/prototype">Case Management</Link></li>
          <li><Link to="/prototype">Employer Pipeline</Link></li>
          <li><Link to="/prototype">Facility Admin</Link></li>
          <li><Link to="/prototype">Training Providers</Link></li>
        </ul>
      </div>
      <div>
        <div className="f-col-t">For Funders</div>
        <ul className="f-links">
          <li><button type="button" className="f-linkbtn" data-track="footer-brief" onClick={onBrief}>Investment Brief (PDF)</button></li>
          <li><a href="mailto:info@hdguniversity.org?subject=Grant%20Pathways">Grant Pathways</a></li>
          <li><a href="#outcomes">Impact Evidence</a></li>
          <li><button type="button" className="f-linkbtn" onClick={onSchedule}>Pilot Program</button></li>
          <li><button type="button" className="f-linkbtn" data-track="footer-schedule" data-testid="footer-schedule-call" onClick={onSchedule}>Schedule a Call</button></li>
        </ul>
      </div>
      <div>
        <div className="f-col-t">Evidence</div>
        <ul className="f-links">
          <li><a href="https://www.rand.org/pubs/research_reports/RR266.html" target="_blank" rel="noreferrer">RAND Meta-Analysis</a></li>
          <li><a href="https://www.jff.org" target="_blank" rel="noreferrer">JFF Market Scan</a></li>
          <li><a href="https://www.ed.gov" target="_blank" rel="noreferrer">Workforce Pell</a></li>
          <li><a href="https://bjs.ojp.gov" target="_blank" rel="noreferrer">BJS Statistics</a></li>
          <li><a href="https://www.vera.org" target="_blank" rel="noreferrer">Vera Institute</a></li>
        </ul>
      </div>
    </div>
    <div className="f-bottom">
      <div className="f-bottom-l">© 2026 HDG University · Tech Tradesmith Institute · All rights reserved</div>
      <div className="f-bottom-r">
        <a href="mailto:info@hdguniversity.org?subject=Privacy">Privacy</a>
        <a href="mailto:info@hdguniversity.org?subject=Terms">Terms</a>
        <a href="mailto:info@hdguniversity.org?subject=Confidentiality">Confidentiality Notice</a>
        <Link to="/login" data-testid="footer-signin">Sign In</Link>
      </div>
    </div>
  </footer>
);
